import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";
import { useScene } from "./SceneController";

const sectionConfig = {
    hero: { color: "#38bdf8", scale: 1, speed: 0.5, distort: 0.25 },
    problem: { color: "#ef4444", scale: 0.9, speed: 0.2, distort: 0.55 },
    control: { color: "#a855f7", scale: 1.2, speed: 0.8, distort: 0.25 },
    solution: { color: "#22c55e", scale: 1.45, speed: 1.2, distort: 0.12 },
    transformation: { color: "#06b6d4", scale: 1.65, speed: 1.5, distort: 0.08 },
    vision: { color: "#ffffff", scale: 1.9, speed: 2, distort: 0.03 },
    cta: { color: "#3b82f6", scale: 2.2, speed: 2.5, distort: 0 },
};

export default function AICore() {
    const mesh = useRef();
    const light = useRef();
    const { sceneState } = useScene();

    useFrame((state, delta) => {
        if (!mesh.current) return;

        const config = sectionConfig[sceneState.section] || sectionConfig.hero;
        const time = state.clock.elapsedTime;

        /* Continuous intelligence movement */
        mesh.current.rotation.y += delta * config.speed;
        mesh.current.rotation.x = Math.sin(time * 0.5) * 0.3;

        /* Smooth size transition */
        const targetScale = config.scale * (1 + Math.sin(time * 2) * 0.03);

        mesh.current.scale.lerp(
            new THREE.Vector3(targetScale, targetScale, targetScale),
            0.05,
        );

        /* Dynamic color transition */
        mesh.current.material.color.lerp(
            new THREE.Color(config.color),
            0.05,
        );

        /* AI processing energy */
        const energy = sceneState.coreIntensity + Math.sin(time * 3) * 0.5;

        mesh.current.material.emissiveIntensity = energy;

        if (light.current) { light.current.intensity = energy * 2; }
    });

    const config = sectionConfig[sceneState.section] || sectionConfig.hero;

    return (
        <group>
            <mesh ref={mesh}>
                <icosahedronGeometry args={[1, 6]} />

                <MeshDistortMaterial
                    color={config.color}
                    emissive={config.color}
                    emissiveIntensity={3}
                    distort={config.distort}
                    speed={config.speed}
                    roughness={0.05}
                    metalness={0.9}
                />
            </mesh>

            {/* Energy beam */}

            <mesh position={[0, 0, 1.1]}>
                <planeGeometry args={[2, 0.04]} />

                <meshBasicMaterial color="#22d3ee" transparent opacity={0.7} />
            </mesh>

            <pointLight
                ref={light}
                color={config.color}
                intensity={6}
                distance={15}
            />
        </group>
    );
}
